import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ShieldCheck, KeyRound, Smartphone, Laptop } from 'lucide-react'
import { usePrivanaI18n } from '@/i18n/PrivanaI18nProvider.jsx'

function MemberSecurityContent({ clubCode, get, request, onLogout, isLoggingOut = false }) {
  const { t } = usePrivanaI18n()
  const sectionRef = useRef(null)
  useLayoutEffect(() => {
    const section = sectionRef.current, main = section?.closest('main')
    const owner = section && getComputedStyle(section).overflowY === 'auto' ? section : main && getComputedStyle(main).overflowY === 'auto' ? main : document.scrollingElement
    if (owner) owner.scrollTop = 0
  }, [])
  const [identity, setIdentity] = useState(null)
  const [loadError, setLoadError] = useState(false)
  const [state, setState] = useState('')
  const [retryAt, setRetryAt] = useState(0)
  const [seconds, setSeconds] = useState(0)
  const [logoutPending, setLogoutPending] = useState(false)
  const [logoutError, setLogoutError] = useState(false)
  const resetBusy = useRef(false), logoutBusy = useRef(false), alive = useRef(true)
  const path = `/api/members-portal/${encodeURIComponent(clubCode)}/security`
  useEffect(() => {
    alive.current = true
    let current = true
    get(path).then(async response => {
      if (!response.ok) throw new Error('security_unavailable')
      const payload = await response.json()
      if (current) setIdentity(payload.data)
    }).catch(() => { if (current) setLoadError(true) })
    return () => { current = false; alive.current = false }
  }, [get, path])
  useEffect(() => {
    const update = () => setSeconds(Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)))
    update()
    if (!retryAt) return
    const timer = setInterval(update, 1000)
    return () => clearInterval(timer)
  }, [retryAt])
  const reset = async () => {
    if (resetBusy.current || retryAt > Date.now() || !identity?.canResetPassword) return
    resetBusy.current = true
    setState('sending')
    try {
      const response = await request(`${path}/password-reset`, { method: 'POST' })
      const payload = await response.json()
      if (!alive.current) return
      if (response.status === 429) {
        const retry = Number(payload.retryAfterSeconds)
        setRetryAt(retry > 0 ? Date.now() + retry * 1000 : 0)
        setState(retry > 0 ? 'rate' : 'rateUnknown')
      } else if (response.ok && payload.ok) {
        setState('accepted')
        setRetryAt(Date.now() + 60000)
      } else setState('failed')
    } catch { if (alive.current) setState('failed') }
    finally { resetBusy.current = false }
  }
  const signOut = async () => {
    if (logoutBusy.current || isLoggingOut || typeof onLogout !== 'function') return
    logoutBusy.current = true
    setLogoutPending(true); setLogoutError(false)
    try { await onLogout() }
    catch { if (alive.current) setLogoutError(true) }
    finally { logoutBusy.current = false; if (alive.current) setLogoutPending(false) }
  }
  const text = key => t(`memberSecurity.${key}`)
  const badge = <span className="mp-security-badge">{text('soon')}</span>
  const pending = logoutPending || isLoggingOut
  return <div ref={sectionRef} className="mp-security">
    <section className="mp-security-card mp-security-intro"><ShieldCheck aria-hidden="true" size={24}/><div><h2>{text('title')}</h2><p>{text('intro')}</p></div></section>
    <div className="mp-security-columns">
      <section className="mp-security-card"><h3><KeyRound aria-hidden="true" size={21}/>{text('details')}</h3>
        <div className="mp-security-email"><span>{text('email')}</span><strong>{identity?.email || (loadError ? text('unavailable') : '…')}</strong></div>
        <hr/><div className="mp-security-row"><span>{text('password')}</span><button type="button" className="mp-chip" onClick={reset} disabled={!identity?.canResetPassword || state === 'sending' || seconds > 0}>{text(state === 'sending' ? 'sending' : 'reset')}</button></div>
        <p>{text('helper')}</p>
        {identity && !identity.canResetPassword ? <p>{text('external')}</p> : null}
        <p className={state === 'failed' || state.startsWith('rate') ? 'mp-error' : ''} role="status" aria-live="polite">{state === 'rate' && seconds > 0 ? t('memberSecurity.rate', { seconds }) : state === 'rate' ? '' : ['accepted','failed','rateUnknown'].includes(state) ? text(state) : ''}</p>
      </section>
      <section className="mp-security-card"><h3><Smartphone aria-hidden="true" size={21}/>{text('twoStep')}</h3>{badge}<p>{text('twoStepHelp')}</p></section>
    </div>
    <section className="mp-security-card"><h3><Laptop aria-hidden="true" size={21}/>{text('devices')}</h3><div className="mp-security-row"><div><strong>{text('thisDevice')}</strong><p>{text('session')}</p></div><button type="button" className="mp-chip" disabled={pending || typeof onLogout !== 'function'} onClick={signOut}>{t(pending ? 'profileOverview.signingOut' : 'profileOverview.signOut')}</button></div>
      {logoutError ? <p className="mp-error" role="alert">{text('logoutError')}</p> : null}<hr/><div className="mp-security-row"><span>{text('otherDevices')}</span>{badge}</div>
    </section>
  </div>
}

export default function MemberSecurity({ scope = '', clubCode, ...props }) {
  return <MemberSecurityContent key={`${scope}:${clubCode}`} clubCode={clubCode} {...props} />
}
